import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useFamily } from '@/hooks/useFamily';
import { formatDateForDB } from '@/lib/dateUtils';

export interface PersonProfile {
  id: string;
  family_id: string;
  first_name: string;
  last_name: string;
  category: 'family' | 'extended_family' | 'friends' | 'medical_other' | 'school' | 'sport' | 'services' | 'professional' | 'pets';
  date_of_birth?: string;
  gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say';
  phone_primary?: string;
  email?: string;
  address?: string;
  avatar_url?: string;
  notes?: string;
  emergency_contact: boolean;
  family_role?: 'father' | 'mother' | 'son' | 'daughter' | 'grandfather' | 'grandmother' | 'guardian_male' | 'guardian_female' | 'parent' | 'spouse' | 'child' | 'grandparent' | 'guardian' | 'pet';
  profile_id?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  medical_speciality?: string;
}

export interface PersonRelationship {
  id: string;
  person_id: string;
  related_person_id?: string;
  family_member_id?: string;
  relationship_type: 'parent' | 'child' | 'spouse' | 'sibling' | 'grandfather' | 'grandmother' | 'grandchild' | 'uncle' | 'aunt' | 'male_cousin' | 'female_cousin' | 'nephew' | 'niece' | 'stepfather' | 'stepmother' | 'brother_in_law' | 'sister_in_law' | 'friend' | 'doctor' | 'teacher' | 'other';
  notes?: string;
  created_at: string;
}

export interface CreatePersonData {
  first_name: string;
  last_name: string;
  category: PersonProfile['category'];
  date_of_birth?: string;
  gender?: PersonProfile['gender'];
  phone_primary?: string;
  email?: string;
  address?: string;
  avatar_url?: string;
  notes?: string;
  emergency_contact?: boolean;
  family_role?: PersonProfile['family_role'];
  medical_speciality?: string;
}

export interface CreatePersonWithRelationData extends CreatePersonData {
  relationship_type?: string;
  relationship?: {
    related_person_id?: string;
    family_member_id?: string;
    relationship_type: PersonRelationship['relationship_type'];
    notes?: string;
  };
}

// Fetch person profiles for a family
async function fetchPersonProfiles(familyId: string): Promise<PersonProfile[]> {
  const { data, error } = await supabase
    .from('person_profiles')
    .select('*')
    .eq('family_id', familyId)
    .order('created_at', { ascending: true });

  if (error) {
    console.error('Error fetching person profiles:', error);
    throw error;
  }

  return data || [];
}

export function useOptimizedPersonProfiles(familyId?: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { toast } = useToast();
  const { profile } = useFamily();

  // Query for person profiles
  const {
    data: rawPersons = [],
    isLoading: loading,
    error,
    refetch: refreshPersons
  } = useQuery({
    queryKey: ['person-profiles', familyId],
    queryFn: () => fetchPersonProfiles(familyId!),
    enabled: !!familyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Deduplicate persons after fetching
  const persons = React.useMemo(() => {
      // Deduplicate persons after fetching using improved logic
      const deduplicatedMap = new Map<string, PersonProfile>();
      
      rawPersons.forEach(person => {
        // Create deduplication key based on profile_id or name combination
        let key: string;
        if (person.profile_id) {
          // For linked profiles, use profile_id as unique key
          key = `profile:${person.profile_id}:${person.family_id}`;
        } else {
          // For non-linked persons, use normalized name + family combination
          const normalizedFirst = person.first_name.toLowerCase().trim();
          const normalizedLast = person.last_name.toLowerCase().trim();
          key = `name:${person.family_id}:${normalizedFirst}:${normalizedLast}`;
        }
        
        // Check if we already have an entry for this key
        const existing = deduplicatedMap.get(key);
        if (!existing) {
          deduplicatedMap.set(key, person);
        } else {
          // Choose the better entry using completeness score
          const current = person;
          const better = chooseBetterPerson(existing, current);
          deduplicatedMap.set(key, better);
        }
      });
    
    return Array.from(deduplicatedMap.values());
  }, [rawPersons]);

  // Helper function to choose the better person profile
  function chooseBetterPerson(a: PersonProfile, b: PersonProfile): PersonProfile {
    // Calculate completeness scores
    const scoreA = calculateCompletenessScore(a);
    const scoreB = calculateCompletenessScore(b);
    
    // Return the one with higher completeness score
    if (scoreA !== scoreB) {
      return scoreA > scoreB ? a : b;
    }
    
    // If scores are equal, prefer the more recently updated one
    return new Date(a.updated_at) > new Date(b.updated_at) ? a : b;
  }

  // Calculate completeness score for deduplication
  function calculateCompletenessScore(person: PersonProfile): number {
    let score = 0;
    
    // Profile_id is most important (linked account)
    if (person.profile_id) score += 15;
    
    // Avatar presence
    if (person.avatar_url) score += 10;
    
    // Personal information
    if (person.date_of_birth) score += 8;
    if (person.email) score += 6;
    if (person.phone_primary) score += 4;
    if (person.address) score += 3;
    if (person.notes) score += 2;
    if (person.family_role) score += 5;
    
    return score;
  }

  // Create person mutation
  const createPersonMutation = useMutation({
    mutationFn: async (personData: CreatePersonWithRelationData) => {
      if (!familyId || !profile) throw new Error('Family ID and profile required');
      
      // Check authentication before proceeding
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('AUTHENTICATION_REQUIRED');
      }

      const { relationship, relationship_type, ...personFields } = personData;
      
      // Normalize date_of_birth - convert empty string to null
      const normalizedFields = { ...personFields };
      if (normalizedFields.date_of_birth === '') {
        normalizedFields.date_of_birth = null;
      }

      const { data: person, error: personError } = await supabase
        .from('person_profiles')
        .insert({
          ...normalizedFields,
          family_id: familyId,
          created_by: profile.id,
        } as any)
        .select()
        .single();

      if (personError) throw personError;

      // If this is the current user creating their own profile, clear the deletion flag
      if (person && (personFields as any).profile_id === profile.id) {
        localStorage.removeItem(`deleted-profile-${profile.id}`);
      }

      // Create relationship if provided (support both old and new format)
      const relationshipData = relationship || (relationship_type && relationship_type !== '' ? { relationship_type: relationship_type as PersonRelationship['relationship_type'] } : null);
      if (relationshipData && person) {
        const { error: relationshipError } = await supabase
          .from('person_relationships')
          .insert({
            person_id: person.id,
            ...relationshipData,
          });

        if (relationshipError) {
          console.error('Error creating relationship:', relationshipError);
        }
      }

      // Sync children to family_members if family category
      if (person && personFields.category === 'family' && personFields.family_role && 
          ['son', 'daughter', 'child'].includes(personFields.family_role)) {
        
        const childAge = personFields.date_of_birth ? 
          new Date().getFullYear() - new Date(personFields.date_of_birth).getFullYear() : null;

        const { error: memberError } = await supabase
          .from('family_members')
          .insert([{
            family_id: familyId,
            child_name: `${personFields.first_name} ${personFields.last_name}`,
            role: personFields.family_role as 'son' | 'daughter' | 'child',
            child_age: childAge,
            child_gender: personFields.gender === 'male' ? 'male' : personFields.gender === 'female' ? 'female' : null,
            child_birth_date: personFields.date_of_birth ? formatDateForDB(new Date(personFields.date_of_birth)) : null,
            child_avatar_url: personFields.avatar_url,
          }]);

        if (memberError) {
          console.error('Error syncing to family_members:', memberError);
        }
      }

      return person;
    },
    onSuccess: async (person) => {
      // Si une date de naissance a été fournie, créer les événements d'anniversaire
      if (person?.date_of_birth && familyId) {
        try {
          const currentYear = new Date().getFullYear();
          // Utiliser la fonction idempotente qui évite les doublons
          await supabase.rpc('create_birthday_events_for_year', {
            _family_id: familyId,
            _year: currentYear
          });
          // Créer aussi pour l'année prochaine
          await supabase.rpc('create_birthday_events_for_year', {
            _family_id: familyId,
            _year: currentYear + 1
          });
        } catch (error) {
          console.error('Error creating birthday events:', error);
          // Ne pas bloquer sur cette erreur
        }
      }
      
      queryClient.invalidateQueries({ queryKey: ['person-profiles', familyId] });
      // Invalider aussi les événements pour afficher les anniversaires
      queryClient.invalidateQueries({ queryKey: ['events', familyId] });
      toast({
        title: "Personne ajoutée",
        description: "La personne a été ajoutée avec succès.",
      });
    },
    onError: (error: any) => {
      console.error('Error creating person:', error);
      
      // Detect specific error types and provide appropriate feedback
      if (error?.message === 'AUTHENTICATION_REQUIRED') {
        toast({
          variant: "destructive",
          title: "Session expirée",
          description: "Votre session a expiré. Veuillez vous reconnecter.",
          action: <button 
            onClick={() => window.dispatchEvent(new CustomEvent('app:navigate', { detail: { path: '/auth', replace: true } }))} 
            className="text-sm underline"
          >
            Se reconnecter
          </button>
        });
      } else if (error?.message?.includes('JWT') || error?.message?.includes('auth') || error?.code === 'PGRST301') {
        toast({
          variant: "destructive", 
          title: "Problème d'authentification",
          description: "Votre session semble invalide. Essayez de vous reconnecter.",
          action: <button 
            onClick={() => {
              supabase.auth.refreshSession().then(({ error }) => {
                if (error) window.dispatchEvent(new CustomEvent('app:navigate', { detail: { path: '/auth', replace: true } }));
              });
            }} 
            className="text-sm underline"
          >
            Actualiser la session
          </button>
        });
      } else if (error?.message?.includes('row-level security') || error?.code === 'PGRST116') {
        toast({
          variant: "destructive",
          title: "Accès refusé", 
          description: "Vous n'avez pas les permissions pour effectuer cette action.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Erreur",
          description: `Impossible d'ajouter la personne: ${error?.message || 'Erreur inconnue'}`,
        });
      }
    },
  });

  // Update person mutation
  const updatePersonMutation = useMutation({
    mutationFn: async ({ personId, updates }: { personId: string; updates: Partial<CreatePersonData> }) => {
      // Filter out non-column fields like relationship_type
      const { relationship_type, ...profileUpdates } = (updates || {}) as any;
      
      // Normalize date_of_birth - convert empty string to null
      const normalizedUpdates = { ...profileUpdates };
      if (normalizedUpdates.date_of_birth === '') {
        normalizedUpdates.date_of_birth = null;
      }

      const { data, error } = await supabase
        .from('person_profiles')
        .update(normalizedUpdates)
        .eq('id', personId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: async (updatedPerson, { updates }) => {
      // Si une date d'anniversaire a été ajoutée ou modifiée, créer/mettre à jour les événements d'anniversaire
      if (updates.date_of_birth && updatedPerson.date_of_birth && familyId) {
        try {
          const currentYear = new Date().getFullYear();
          // Utiliser la fonction idempotente qui évite les doublons
          await Promise.all([
            supabase.rpc('create_birthday_events_for_year', { 
              _family_id: familyId,
              _year: currentYear 
            }),
            supabase.rpc('create_birthday_events_for_year', { 
              _family_id: familyId,
              _year: currentYear + 1 
            })
          ]);
        } catch (error) {
          console.error('Error creating birthday events:', error);
          // Ne pas bloquer sur cette erreur
        }
      }
      
      queryClient.invalidateQueries({ queryKey: ['person-profiles', familyId] });
      // Also invalidate events to refresh birthday events with updated ages
      queryClient.invalidateQueries({ queryKey: ['events', familyId] });
      toast({
        title: "Personne modifiée",
        description: "La personne a été modifiée avec succès.",
      });
    },
    onError: (error) => {
      console.error('Error updating person:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de modifier la personne.",
      });
    },
  });

  // Delete person mutation
  const deletePersonMutation = useMutation({
    mutationFn: async (personId: string) => {
      const { error } = await supabase
        .from('person_profiles')
        .delete()
        .eq('id', personId);

      if (error) throw error;
      return personId;
    },
    onSuccess: (deletedPersonId) => {
      // Check if the deleted person was the current user's own profile
      const deletedPerson = persons.find(p => p.id === deletedPersonId);
      if (deletedPerson && deletedPerson.profile_id && profile && deletedPerson.profile_id === profile.id) {
        // Mark that user has explicitly deleted their own profile
        localStorage.setItem(`deleted-profile-${profile.id}`, 'true');
      }
      
      queryClient.invalidateQueries({ queryKey: ['person-profiles', familyId] });
      toast({
        title: "Personne supprimée",
        description: "La personne a été supprimée avec succès.",
      });
    },
    onError: (error) => {
      console.error('Error deleting person:', error);
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Impossible de supprimer la personne.",
      });
    },
  });

  // Utility functions
  const getPersonsByCategory = (category: PersonProfile['category']) => {
    return persons.filter(person => person.category === category);
  };

  const getPersonsByFamilyRole = (role: PersonProfile['family_role']) => {
    return persons.filter(person => person.family_role === role);
  };

  const getFamilyMembers = () => {
    return persons.filter(person => person.category === 'family');
  };

  const getParents = () => {
    return persons.filter(person => 
      person.family_role && ['father', 'mother', 'parent', 'guardian_male', 'guardian_female'].includes(person.family_role)
    );
  };

  const getChildren = () => {
    return persons.filter(person => 
      person.family_role && ['son', 'daughter', 'child'].includes(person.family_role)
    );
  };

  const getEmergencyContacts = () => {
    return persons.filter(person => person.emergency_contact);
  };

  const searchPersons = (query: string) => {
    const lowercaseQuery = query.toLowerCase();
    return persons.filter(person =>
      person.first_name.toLowerCase().includes(lowercaseQuery) ||
      person.last_name.toLowerCase().includes(lowercaseQuery) ||
      (person.notes && person.notes.toLowerCase().includes(lowercaseQuery))
    );
  };

  return {
    persons,
    loading,
    error,
    createPerson: createPersonMutation.mutate,
    updatePerson: (personId: string, updates: Partial<CreatePersonData>) => 
      updatePersonMutation.mutate({ personId, updates }),
    deletePerson: deletePersonMutation.mutate,
    isCreating: createPersonMutation.isPending,
    isUpdating: updatePersonMutation.isPending,
    isDeleting: deletePersonMutation.isPending,
    refreshPersons,
    getPersonsByCategory,
    getPersonsByFamilyRole,
    getFamilyMembers,
    getParents,
    getChildren,
    getEmergencyContacts,
    searchPersons,
  };
}